import { MapPin, Star, Phone, Globe, DollarSign } from "lucide-react";
import type { ToolWidgetProps } from "./index";

interface Category {
  id: number;
  name: string;
  icon?: string;
}

interface Location {
  formatted_address?: string;
  locality?: string;
  region?: string;
  country?: string;
}

interface Place {
  fsq_place_id: string;
  name: string;
  categories?: Category[];
  location?: Location;
  distance?: number;
  rating?: number;
  price?: number;
  phone?: string;
  website?: string;
}

interface FoursquarePlacesData {
  results: Place[];
  count: number;
}

function PriceLevel({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(4)].map((_, i) => (
        <DollarSign
          key={i}
          className={`w-3 h-3 ${i < level ? "text-green-600" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

export function FoursquarePlacesWidget({
  data,
  count,
}: ToolWidgetProps<FoursquarePlacesData>) {
  const { results = [] } = data || {};

  if (!results.length) {
    return (
      <div className="text-sm text-slate-500 italic">Aucun lieu trouvé</div>
    );
  }

  return (
    <div className="space-y-2">
      {results.map((place) => (
        <div
          key={place.fsq_place_id}
          className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 truncate">
                {place.name}
              </div>
              {place.categories && place.categories.length > 0 && (
                <div className="text-xs text-slate-500 truncate">
                  {place.categories.map((c) => c.name).join(", ")}
                </div>
              )}
            </div>
            {place.rating && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium text-slate-700">
                  {place.rating / 10}
                </span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {place.location?.formatted_address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate max-w-xs">
                  {place.location.formatted_address}
                </span>
              </span>
            )}
            {place.distance && (
              <span className="text-slate-400">
                {place.distance < 1000
                  ? `${Math.round(place.distance)}m`
                  : `${(place.distance / 1000).toFixed(1)}km`}
              </span>
            )}
            {place.price && <PriceLevel level={place.price} />}
            {place.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {place.phone}
              </span>
            )}
            {place.website && (
              <a
                href={place.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-default-600 hover:text-default-700"
              >
                <Globe className="w-3 h-3" />
                Site web
              </a>
            )}
          </div>
        </div>
      ))}
      {count && count > results.length && (
        <div className="text-xs text-slate-400 text-center pt-1">
          + {count - results.length} autres lieux
        </div>
      )}
    </div>
  );
}
